import axios from 'axios';

// Default to empty string (relative URLs) for production - nginx proxies to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const CACHE_KEY = 'tenants_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface Tenant {
  id: string;
  name: string;
  api_url: string;
}

export interface UserTenantsResponse {
  user_id: string;
  default_tenant_id: string;
  authorized_tenants: Tenant[];
  deployment_mode?: 'single' | 'multi';
}

class TenantService {
  private static instance: TenantService;
  private cache: { data: UserTenantsResponse; timestamp: number } | null = null;

  private constructor() {
    // Load from localStorage
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) {
        this.cache = parsed;
      } else {
        localStorage.removeItem(CACHE_KEY);
      }
    }
  }

  public static getInstance(): TenantService {
    if (!TenantService.instance) {
      TenantService.instance = new TenantService();
    }
    return TenantService.instance;
  }

  public async getUserTenants(forceRefresh = false): Promise<UserTenantsResponse> {
    // Return cached if valid and not forcing refresh
    if (!forceRefresh && this.cache && Date.now() - this.cache.timestamp < CACHE_DURATION) {
      return this.cache.data;
    }

    // Fetch from API
    const response = await axios.get<UserTenantsResponse>(`${API_BASE_URL}/api/tenants/me`);

    // Update cache
    this.cache = {
      data: response.data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));

    return response.data;
  }

  public clearCache(): void {
    this.cache = null;
    localStorage.removeItem(CACHE_KEY);
  }
}

export default TenantService;
